import React, { useState, useEffect } from 'react';
import { ReactiveJsonRoot } from '@ea-lab/reactive-json';
import { AnalyticsPage } from './AnalyticsPage';

async function fetchJson(url) {
  const res = await fetch(url, { credentials: 'include' });
  if (!res.ok) throw new Error(`Fetch ${url} failed (${res.status})`);
  return res.json();
}

// Users: flat objects (no data wrapper)
function processUsers(raw) {
  const items = Array.isArray(raw) ? raw : raw?.items || [];
  return items
    .filter(u => u.name && u.active !== false)
    .map(u => ({
      id: u.id,
      name: u.name,
      email: u.email || null,
    }));
}

// Projects: envelope { item_url, data: {...} }
function processProjects(raw) {
  const items = Array.isArray(raw) ? raw : raw?.items || [];
  return items.map(p => {
    const d = p.data || p;
    return {
      id: d.id,
      name: d.name || 'Sans nom',
      methodology: d.methodology || null,
      memberIds: d.memberIds || [],
    };
  });
}

// Tasks: envelope { item_url, data: {...} }
function processTasks(raw) {
  const items = Array.isArray(raw) ? raw : raw?.items || [];
  return items.map(t => {
    const d = t.data || t;
    return {
      id: d.id,
      title: d.title || 'Sans titre',
      status: d.status || 'Non défini',
      type: d.type || 'Tâche',
      priority: d.priority || 0,
      complexity: d.complexity || 0,
      assignee: d.assignee || null,
      estimation: d.estimation || d.allocatedTime || 0,
      timeSpent: d.timeSpent || 0,
      raf: d.raf || 0,
      projectId: d.projectId || null,
      parentId: d.parentTaskId || null,
      tags: Array.isArray(d.tags) ? d.tags : [],
      profile: d.profile || null,
      returnType: d.returnType || null,
      isUrgent: d.isUrgent || false,
      dueDate: d.dueDate || null,
      author: d.author || null,
      createdAt: d.createdAt || null,
      updatedAt: d.updatedAt || null,
    };
  });
}

// Time logs: envelope { item_url, data: {...} }
function processTimeLogs(raw) {
  const items = Array.isArray(raw) ? raw : raw?.items || [];
  return items.map(l => {
    const d = l.data || l;
    return {
      id: d.id,
      timeSpent: d.timeSpent || 0,
      timeLeft: d.timeLeft || 0,
      date: d.date || null,
      description: d.description || null,
      taskId: d.taskId || null,
      projectId: d.projectId || null,
      user: d.user || null,
    };
  });
}

// ─── Fallback data when API is unavailable ───
const FALLBACK_TASKS = [
  { id: 1, title: 'Système de notifications', status: 'Backlog', type: 'Évolution', priority: 3, complexity: 5, assignee: 'hugo.moreau', estimation: 24, timeSpent: 4, raf: 20, projectId: '1', parentId: null, tags: ['Backend','Feature'], profile: 'Back-End', returnType: null, isUrgent: false, dueDate: '2026-04-22', author: 'hugo.moreau', createdAt: '2026-02-20T10:00:00', updatedAt: '2026-02-28T15:00:00' },
  { id: 2, title: 'OAuth2 - Endpoint /authorize', status: 'Terminé', type: 'Sub-task', priority: 3, complexity: 2, assignee: 'hugo.moreau', estimation: 8, timeSpent: 6, raf: 2, projectId: '1', parentId: '1', tags: ['Backend'], profile: 'Back-End', returnType: null, isUrgent: false, dueDate: null, author: 'hugo.moreau', createdAt: '2026-02-20T10:00:00', updatedAt: '2026-02-25T10:00:00' },
  { id: 3, title: 'Refonte page d\'accueil', status: 'En cours', type: 'Évolution', priority: 5, complexity: 8, assignee: 'marie.dupont', estimation: 40, timeSpent: 18, raf: 22, projectId: '1', parentId: null, tags: ['Frontend','UX'], profile: 'Front-End', returnType: "Demande d'évolution", isUrgent: false, dueDate: '2026-03-15', author: 'marie.dupont', createdAt: '2026-02-10T09:00:00', updatedAt: '2026-02-28T11:00:00' },
  { id: 4, title: 'Fix pagination API', status: 'Terminé', type: 'Bug', priority: 7, complexity: 2, assignee: 'jean.martin', estimation: 4, timeSpent: 3, raf: 0, projectId: '1', parentId: null, tags: ['Backend','Bug'], profile: 'Back-End', returnType: 'Bug - Fonctionnel', isUrgent: true, dueDate: '2026-02-22', author: 'jean.martin', createdAt: '2026-02-18T14:00:00', updatedAt: '2026-02-22T16:00:00' },
  { id: 5, title: 'Mise en place CI/CD', status: 'Prêt à développer', type: 'Tâche', priority: 3, complexity: 5, assignee: 'pierre.durand', estimation: 16, timeSpent: 0, raf: 16, projectId: '1', parentId: null, tags: ['DevOps'], profile: 'DevOps', returnType: null, isUrgent: false, dueDate: '2026-03-30', author: 'pierre.durand', createdAt: '2026-02-25T08:00:00', updatedAt: '2026-02-25T08:00:00' },
  { id: 6, title: 'Optimisation requêtes SQL', status: 'En cours', type: 'Tâche préventive', priority: 5, complexity: 3, assignee: 'jean.martin', estimation: 12, timeSpent: 6, raf: 6, projectId: '1', parentId: null, tags: ['Backend','Performance'], profile: 'Back-End', returnType: null, isUrgent: false, dueDate: '2026-03-10', author: 'jean.martin', createdAt: '2026-02-15T10:00:00', updatedAt: '2026-02-27T14:00:00' },
  { id: 7, title: 'Tests unitaires module auth', status: 'Terminé', type: 'Sub-task', priority: 3, complexity: 2, assignee: 'sophie.bernard', estimation: 8, timeSpent: 7, raf: 1, projectId: '1', parentId: '1', tags: ['Test'], profile: 'Testeur', returnType: null, isUrgent: false, dueDate: null, author: 'sophie.bernard', createdAt: '2026-02-21T09:00:00', updatedAt: '2026-02-26T17:00:00' },
  { id: 8, title: 'Bug affichage mobile menu', status: 'Bloqué', type: 'Bug', priority: 5, complexity: 3, assignee: 'marie.dupont', estimation: 6, timeSpent: 2, raf: 4, projectId: '1', parentId: null, tags: ['Frontend','Bug'], profile: 'Front-End', returnType: 'Bug : Mise en page / Design', isUrgent: false, dueDate: '2026-03-05', author: 'marie.dupont', createdAt: '2026-02-24T11:00:00', updatedAt: '2026-02-27T09:00:00' },
  { id: 9, title: 'Documentation API REST', status: 'Demande', type: 'Tâche de gestion', priority: 1, complexity: 3, assignee: 'hugo.moreau', estimation: 16, timeSpent: 0, raf: 16, projectId: '1', parentId: null, tags: ['Documentation'], profile: 'Lead Dev', returnType: null, isUrgent: false, dueDate: '2026-04-01', author: 'hugo.moreau', createdAt: '2026-02-26T15:00:00', updatedAt: '2026-02-26T15:00:00' },
  { id: 10, title: 'Migration base PostgreSQL', status: 'Validé', type: 'MEP', priority: 5, complexity: 8, assignee: 'pierre.durand', estimation: 32, timeSpent: 28, raf: 4, projectId: '1', parentId: null, tags: ['DevOps','Database'], profile: 'DevOps', returnType: null, isUrgent: false, dueDate: '2026-03-01', author: 'pierre.durand', createdAt: '2026-02-05T09:00:00', updatedAt: '2026-02-28T10:00:00' },
  { id: 11, title: 'Retour client - Formulaire contact', status: 'En cours', type: 'Retour client', priority: 5, complexity: 2, assignee: 'sophie.bernard', estimation: 4, timeSpent: 2, raf: 2, projectId: '1', parentId: null, tags: ['Frontend'], profile: 'Front-End', returnType: 'Bug - Fonctionnel', isUrgent: false, dueDate: '2026-03-03', author: 'sophie.bernard', createdAt: '2026-02-27T10:00:00', updatedAt: '2026-02-28T09:00:00' },
  { id: 12, title: 'Audit sécurité dépendances', status: 'Backlog', type: 'Tâche préventive', priority: 3, complexity: 3, assignee: 'jean.martin', estimation: 8, timeSpent: 0, raf: 8, projectId: '1', parentId: null, tags: ['Sécurité'], profile: 'DevOps', returnType: null, isUrgent: false, dueDate: null, author: 'jean.martin', createdAt: '2026-02-28T08:00:00', updatedAt: '2026-02-28T08:00:00' },
  { id: 13, title: 'Support utilisateur - Login SSO', status: 'Terminé', type: 'Support', priority: 7, complexity: 1, assignee: 'sophie.bernard', estimation: 2, timeSpent: 1, raf: 0, projectId: '1', parentId: null, tags: ['Support'], profile: 'Back-End', returnType: null, isUrgent: true, dueDate: '2026-02-20', author: 'sophie.bernard', createdAt: '2026-02-19T16:00:00', updatedAt: '2026-02-20T11:00:00' },
  { id: 14, title: 'Retour interne - Perf dashboard', status: 'Prêt à développer', type: 'Retour interne', priority: 3, complexity: 4, assignee: 'hugo.moreau', estimation: 12, timeSpent: 0, raf: 12, projectId: '1', parentId: null, tags: ['Frontend','Performance'], profile: 'Front-End', returnType: 'Bug : Performance', isUrgent: false, dueDate: '2026-03-20', author: 'hugo.moreau', createdAt: '2026-02-27T14:00:00', updatedAt: '2026-02-27T14:00:00' },
  { id: 15, title: 'Recommandation architecture micro-services', status: 'Demande', type: 'Recommandation', priority: 1, complexity: 5, assignee: 'pierre.durand', estimation: 20, timeSpent: 0, raf: 20, projectId: '1', parentId: null, tags: ['Architecture'], profile: 'Architecte', returnType: null, isUrgent: false, dueDate: null, author: 'pierre.durand', createdAt: '2026-02-28T09:00:00', updatedAt: '2026-02-28T09:00:00' },
];

const FALLBACK_PROJECTS = [
  { id: 1, name: 'Projet Alpha', methodology: '2', memberIds: ['1','2','3','4','5'] },
];

const FALLBACK_USERS = [
  { id: 2, name: 'marie.dupont', email: 'marie.dupont@orbit.test' },
  { id: 3, name: 'jean.martin', email: 'jean.martin@orbit.test' },
  { id: 4, name: 'hugo.moreau', email: 'hugo.moreau@orbit.test' },
  { id: 5, name: 'sophie.bernard', email: 'sophie.bernard@orbit.test' },
  { id: 6, name: 'pierre.durand', email: 'pierre.durand@orbit.test' },
];

const FALLBACK_TIMELOGS = [
  { id: 1, timeSpent: 4, timeLeft: 0, date: '2026-02-26', description: 'Implémentation auth', taskId: '2', projectId: '1', user: 'jean.martin' },
  { id: 2, timeSpent: 4, timeLeft: 0, date: '2026-02-27', description: 'Tests auth', taskId: '2', projectId: '1', user: 'jean.martin' },
  { id: 3, timeSpent: 6, timeLeft: 2, date: '2026-02-26', description: 'Refonte header', taskId: '3', projectId: '1', user: 'marie.dupont' },
  { id: 4, timeSpent: 3, timeLeft: 0, date: '2026-02-25', description: 'Fix pagination', taskId: '4', projectId: '1', user: 'jean.martin' },
  { id: 5, timeSpent: 8, timeLeft: 4, date: '2026-02-27', description: 'Migration DB', taskId: '10', projectId: '1', user: 'pierre.durand' },
  { id: 6, timeSpent: 2, timeLeft: 4, date: '2026-02-28', description: 'Bug mobile menu', taskId: '8', projectId: '1', user: 'marie.dupont' },
];

export default function AnalyticsPageWrapper({ plugins }) {
  const [tasks, setTasks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [timeLogs, setTimeLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [usingFallback, setUsingFallback] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const [rawTasks, rawProjects, rawUsers, rawTimeLogs] = await Promise.allSettled([
          fetchJson('/decoupled-api/node/task/collection?display=decoupled'),
          fetchJson('/decoupled-api/group/project/collection?display=decoupled&filter[0][f]=is_template&filter[0][v]=1&filter[0][c]=%3C%3E'),
          fetchJson('/decoupled-api/user/user/collection?display=decoupled'),
          fetchJson('/decoupled-api/node/time_log/collection?display=decoupled'),
        ]);
        if (cancelled) return;

        const loadedTasks = rawTasks.status === 'fulfilled' ? processTasks(rawTasks.value) : [];
        const loadedProjects = rawProjects.status === 'fulfilled' ? processProjects(rawProjects.value) : [];
        const loadedUsers = rawUsers.status === 'fulfilled' ? processUsers(rawUsers.value) : [];
        const loadedTimeLogs = rawTimeLogs.status === 'fulfilled' ? processTimeLogs(rawTimeLogs.value) : [];

        // Use fallback if no tasks loaded from API
        if (loadedTasks.length === 0) {
          setTasks(FALLBACK_TASKS);
          setProjects(loadedProjects.length > 0 ? loadedProjects : FALLBACK_PROJECTS);
          setUsers(loadedUsers.length > 0 ? loadedUsers : FALLBACK_USERS);
          setTimeLogs(loadedTimeLogs.length > 0 ? loadedTimeLogs : FALLBACK_TIMELOGS);
          setUsingFallback(true);
        } else {
          setTasks(loadedTasks);
          setProjects(loadedProjects);
          setUsers(loadedUsers);
          setTimeLogs(loadedTimeLogs);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message);
          setTasks(FALLBACK_TASKS);
          setProjects(FALLBACK_PROJECTS);
          setUsers(FALLBACK_USERS);
          setTimeLogs(FALLBACK_TIMELOGS);
          setUsingFallback(true);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <>
        <ReactiveJsonRoot rjBuildUrl="/components/layout/Navigation.yaml" rjBuildFetchMethod="GET" plugins={plugins} />
        <div className="min-h-screen bg-[color:var(--surface-page)] flex items-center justify-center">
          <div className="flex flex-col items-center gap-3">
            <div className="w-10 h-10 border-4 border-iris-200 border-t-iris-600 rounded-full animate-spin"></div>
            <p className="text-sm text-slate-500">Chargement des données d'analyse...</p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <ReactiveJsonRoot rjBuildUrl="/components/layout/Navigation.yaml" rjBuildFetchMethod="GET" plugins={plugins} />
      <div className="min-h-screen bg-[color:var(--surface-page)]">
        {error && (
          <div className="mx-6 mt-4 bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 rounded-lg text-sm">
            Certaines données n'ont pas pu être chargées : {error}
          </div>
        )}
        {usingFallback && (
          <div className="mx-6 mt-4 bg-sky-50 border border-sky-200 text-sky-800 px-4 py-3 rounded-lg text-sm flex items-center gap-2">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
            Données de démonstration — L'API backend n'a pas retourné de données, les graphiques utilisent un jeu de données d'exemple.
          </div>
        )}
        <AnalyticsPage tasks={tasks} projects={projects} users={users} timeLogs={timeLogs} />
      </div>
    </>
  );
}

